import { ICellRendererParams } from "ag-grid-community";
import { RelocationItemInput } from "@/gql/graphql";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { SupplierTripCreateDialog } from "@/app/SupplierTrips/Components/SupplierTripCreateDialog";
import { OfficeCreateDialog } from "@/app/Offices/Components/OfficeCreateDialog";
import { PlusIcon } from "@heroicons/react/16/solid";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { supplierOfficeListQuery } from "@/app/Offices/GraphQL/supplierOfficeListQuery";
import { supplierTripByOfficesQuery } from "@/app/SupplierTrips/GraphQL/supplierTripRecord";
import { cn } from "@/lib/utils";
import { getImportOfficeQueryVariables } from "@/app/Import/Utils/getImportTableQueryVariables";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export type OfficeType = "departure" | "delivery";

export function OfficeRenderCell(
  props: ICellRendererParams<RelocationItemInput>,
) {
  const cellValue = props.valueFormatted ? props.valueFormatted : props.value;

  const supplierId = props.colDef?.cellRendererParams?.supplier?.id;
  const officeType: OfficeType = props.colDef?.cellRendererParams?.officeType;

  const { data } = useGqlQuery(
    supplierOfficeListQuery,
    getImportOfficeQueryVariables(supplierId),
  );

  const offices = data?.supplierOffices.data ?? [];

  //Check if the code was a number or a string
  const departureCode = props.data?.departure_office_code;

  const departureOffice = departureCode
    ? offices.find((office) => {
        return office.code === departureCode;
      })
    : null;

  const deliveryCode = props.data?.delivery_office_code;

  const deliveryOffice = deliveryCode
    ? offices.find((office) => {
        return office.code === deliveryCode;
      })
    : null;

  const noOffice =
    officeType === "departure" ? !departureOffice : !deliveryOffice;

  const { open } = useDialog(SupplierTripCreateDialog);
  const { open: createOffice } = useDialog(OfficeCreateDialog);

  const { data: trip } = useGqlQuery(
    supplierTripByOfficesQuery,
    {
      officeAId: departureOffice?.id ?? "",
      officeBId: deliveryOffice?.id ?? "",
    },
    {
      enabled: !!departureOffice && !!deliveryOffice,
    },
  );

  const noTrip = trip?.supplierTripByOffices === null;

  if (!cellValue) {
    return null;
  }

  return (
    <div
      className={cn("flex h-full w-full items-center justify-between gap-2", {
        "bg-red-50 text-red-900": noOffice,
        "bg-amber-50 text-amber-900": noTrip,
      })}
    >
      <span className="font-medium">{cellValue}</span>

      {noOffice ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="inline-flex shrink-0 items-center gap-1 rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700 transition-colors hover:bg-red-200 focus:outline-none"
              onClick={(e) => {
                e.stopPropagation();
                createOffice({
                  initialValues: {
                    supplier: {
                      connect: supplierId,
                    },
                    code:
                      officeType === "departure"
                        ? props.data?.departure_office_code
                        : props.data?.delivery_office_code,
                  },
                });
              }}
            >
              <PlusIcon className="h-3 w-3" />
              Office
            </button>
          </TooltipTrigger>
          <TooltipContent>Office missing - click to create</TooltipContent>
        </Tooltip>
      ) : null}

      {noTrip ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="inline-flex shrink-0 items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-200 focus:outline-none"
              onClick={(e) => {
                e.stopPropagation();
                open({
                  supplierId: supplierId,
                  initialValues: {
                    officeA: {
                      connect: departureOffice!.id,
                    },
                    officeB: {
                      connect: deliveryOffice!.id,
                    },
                    hire_units_allowed: props.data?.hire_units_allowed ?? null!,
                    extra_hire_units_allowed:
                      props.data?.extra_hire_units_allowed ?? null!,
                  },
                });
              }}
            >
              <PlusIcon className="h-3 w-3" />
              Trip
            </button>
          </TooltipTrigger>
          <TooltipContent>Trip missing - click to create</TooltipContent>
        </Tooltip>
      ) : null}
    </div>
  );
}
